import { useMemo } from "react";
import { useFormattedReference } from "@hooks/useFormattedReference";
import {
  DRY_BULK_NOMINATION_QUERY_KEY,
  getDryBulkCompanies,
  getDryBulkStockPiles,
} from "@services/dryBulkNominationApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Cargo } from "@services/vessel-nomination/types";
import type { Port } from "@services/vessel/types";
import { useQuery } from "@tanstack/react-query";

export interface DryBulkOption {
  label: string;
  value: string;
}

/** Dry-bulk cargo types only (Jira 7418): raw (BR), bagged (BA), containerised (BC). */
export const DRY_BULK_CARGO_REFERENCE = `${REFERENCE_DATA_TYPES.CARGO}?cargoTypeCodes=BR,BA,BC`;

export const codeLabel = (code: string, name?: string | null) =>
  name ? `${code} - ${name}` : code;

const labelCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const byLabel = (a: DryBulkOption, b: DryBulkOption) =>
  labelCollator.compare(a.label, b.label);

export const labelFor = (options: DryBulkOption[], value?: string | null) =>
  options.find((option) => option.value === value)?.label ?? value ?? "";

export const useDryBulkReferenceOptions = () => {
  const { options: cargo } = useFormattedReference<Cargo>(DRY_BULK_CARGO_REFERENCE, "bff-vessel");
  const { options: ports } = useFormattedReference<Port>(REFERENCE_DATA_TYPES.PORTS, "bff-vessel");
  const { data: companies = [] } = useQuery({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, "companies"],
    queryFn: getDryBulkCompanies,
    staleTime: Infinity,
  });
  const { data: stockPiles = [] } = useQuery({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, "stock-piles"],
    queryFn: getDryBulkStockPiles,
    staleTime: Infinity,
  });

  const cargoOptions = useMemo(
    () =>
      cargo
        .map((item) => ({
          label: codeLabel(String(item.cargoCode), item.cargoDescription),
          value: String(item.cargoCode),
        }))
        .sort(byLabel),
    [cargo],
  );
  const portOptions = useMemo(
    () =>
      ports
        .filter((port) => port.portCode)
        .map((port) => ({
          label: codeLabel(String(port.portCode), port.portName),
          value: String(port.portCode),
        }))
        .sort(byLabel),
    [ports],
  );
  const companyOption = (company: { companyCode: string; companyNameEnglish?: string }) => ({
    label: codeLabel(company.companyCode, company.companyNameEnglish),
    value: company.companyCode,
  });
  const shipperOptions = useMemo(
    () =>
      companies
        .filter((company) => company.isDrybulkCompany === true)
        .map(companyOption)
        .sort(byLabel),
    [companies],
  );
  const receiverOptions = useMemo(
    () =>
      companies
        .filter((company) => company.isImporterCompany === true)
        .map(companyOption)
        .sort(byLabel),
    [companies],
  );
  const companyOptions = useMemo(() => companies.map(companyOption).sort(byLabel), [companies]);
  const { options: natureOfCalls } = useFormattedReference<{ code: string; description: string }>(
    REFERENCE_DATA_TYPES.NATURE_OF_CALLS,
    "bff-vessel",
  );
  const purposeOfVisitOptions = useMemo(
    () =>
      natureOfCalls
        .filter((item) => item.code)
        .map((item) => ({
          label: codeLabel(String(item.code), item.description),
          value: String(item.code),
        }))
        .sort(byLabel),
    [natureOfCalls],
  );
  const stockPileOptions = useMemo(
    () =>
      stockPiles
        .map((pile) => ({
          label: codeLabel(pile.stockPileCode, pile.stockPileName),
          value: pile.stockPileCode,
        }))
        .sort(byLabel),
    [stockPiles],
  );

  return {
    cargoOptions,
    portOptions,
    companyOptions,
    shipperOptions,
    receiverOptions,
    purposeOfVisitOptions,
    stockPileOptions,
  };
};
